import { EOnboardingVariant } from "@/enum/onboarding-variant.enum";

import { User } from "@/pages/Public/pages/Login/schema/login";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";
import { ROUTE_CONFIG } from "@/routes/config";

/**
 * Legacy derivation, kept for the few responses that replace the cached user
 * without carrying the server-derived variant (e.g. the 2FA toggles). It reads
 * `company.isOnboarded`, which is stamped `true` on staff-created demo
 * workspaces at creation time — so it can mistake an unset-up demo workspace
 * for a finished one. The API field is authoritative whenever it is present.
 */
const deriveFromLegacyFields = (user: User): EOnboardingVariant => {
	if (user.isOnboarded) {
		return EOnboardingVariant.NONE;
	}

	if (user.company?.isOnboarded) {
		return EOnboardingVariant.SLIM;
	}

	return user.invitedAt || user.company?.isDemo
		? EOnboardingVariant.FULL_NO_CHECKOUT
		: EOnboardingVariant.FULL;
};

export const resolveOnboardingVariant = (user?: User | null): EOnboardingVariant | undefined => {
	if (!user) {
		return undefined;
	}

	return user.onboardingVariant ?? deriveFromLegacyFields(user);
};

/** Which onboarding the logged-in user still has to run (PREC-3719). */
export const useOnboardingVariant = (): EOnboardingVariant | undefined => {
	const { user } = useAppSelector(authSelector);

	return resolveOnboardingVariant(user);
};

/** True while the user still owes the full wizard but must not be charged (V2). */
export const skipsCheckout = (variant?: EOnboardingVariant): boolean =>
	variant === EOnboardingVariant.FULL_NO_CHECKOUT;

/**
 * Where an unfinished onboarding has to continue, or `null` when the app may
 * render. Every private layout and both wizard shells route on this, so the
 * rule lives in exactly one place.
 */
export const onboardingRedirectPath = (variant?: EOnboardingVariant): string | null => {
	switch (variant) {
		case EOnboardingVariant.FULL:
		case EOnboardingVariant.FULL_NO_CHECKOUT:
			return ROUTE_CONFIG.ONBOARDING;
		case EOnboardingVariant.SLIM:
			return ROUTE_CONFIG.ONBOARDING_V3;
		default:
			return null;
	}
};

export const useOnboardingRedirectPath = (): string | null =>
	onboardingRedirectPath(useOnboardingVariant());
